import { TemplateResult, LitElement, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { AdoptedStyleSheets } from '../../controllers/adopted-stylesheets';
import encapsulatedStyles from './styles/outline-mobile-menu.inner.pcss?inline';
import globalStyles from './styles/outline-mobile-menu.global.pcss?inline';
import { classMap } from 'lit/directives/class-map.js';
import './../outline-svg/outline-svg';
import { MobileController } from '../../controllers/mobile-controller';

export const mobileMenuVariants = ['default', 'locations', 'filters'] as const;
export type MenuVariant = (typeof mobileMenuVariants)[number];

/**
 * Outline Mobile Menu component
 *
 * @slot - The mobile menu items
 * @slot button-label - Text for mobile button
 */

@customElement('outline-mobile-menu')
export class OutlineMobileMenu extends LitElement {
  adoptedStyleSheets = new AdoptedStyleSheets(this, {
    encapsulatedCSS: encapsulatedStyles,
    globalCSS: globalStyles,
  });

  mobileController = new MobileController(this, 'lg');

  @property({ type: Boolean, attribute: 'open', reflect: true })
  open: boolean;

  @property({ type: String, attribute: 'open-icon' })
  openIcon = 'chevronUp';

  @property({ type: String, attribute: 'close-icon' })
  closeIcon ='chevronDown';

  @property({ type: String, attribute: 'svg-size' })
  svgSize ='16px';

  @property({type: String, reflect: true})
  variant: MenuVariant = 'default';

  @property({ type: Boolean, reflect: true })
  isJumpnav = false;

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener('keyup', this.escapeHandler);
  }

  disconnectedCallback(): void {
      window.removeEventListener('keyup', this.escapeHandler);
  }

  escapeHandler(e: KeyboardEvent) {
    const self = document.querySelector('outline-mobile-menu') as OutlineMobileMenu;
    if (self.open && e.key === 'Escape') {
      self.handleToggleMenu();
    }
  }

  updated() {
    if (this.variant=== 'filters' && !this.mobileController.isMobile) {
      this.removeMobileScrollLocked();
      this.open = false;
    }
  }

  handleToggleMenu() {
    this.open = !this.open;
    if (this.variant === 'filters') {
      this.open
      ? this.addMobileScrollLocked()
      : this.removeMobileScrollLocked();
    }
  }

  render(): TemplateResult {
    const menuClasses = {
      'mobile-menu': true,
      'open': this.open,
    };

    return html`
      <div class="${classMap(menuClasses)}">
        <span class="label">
          <slot name="label"></slot>
        </span>
        <div class="menu">
          ${this.buttonTemplate()}
          <div class="button-offset"></div>
        </div>
        <div class="menu-content">
          ${this.variant === 'filters' ? this.buttonTemplate('filter-close') : null}
          <slot></slot>
        </div>
      </div>
    `;
  }

  buttonTemplate(secondaryClass?:string) {
    return html`
      <button
        class="menu-button ${secondaryClass}"
        @click=${this.handleToggleMenu}
        tabindex=${this.variant === 'locations' ? "-1" : "0"}
        type="button"
      >
        <span class="button-label">
          <slot name="button-label"></slot>
        </span>
        <outline-svg
          slot="icon"
          name="${this.open ? this.openIcon : this.closeIcon}"
          size="${this.svgSize}"
          decorative="false"
          svg-fill="var(--menu-icon-svg-fill)"
          svg-color="var(--menu-icon-svg-color)"
          tabindex=${this.variant === 'locations' ? "0" : "-1"}
          aria-label=${this.variant === 'locations' ?  'open menu': '' }
        ></outline-svg>
      </button>`
  }

  addMobileScrollLocked() {
    document.body.classList.add('mobile-scroll-locked');
  }

  removeMobileScrollLocked() {
    document.body.classList.remove('mobile-scroll-locked');
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'outline-mobile-menu': OutlineMobileMenu;
  }
}
